import React, { useState, useEffect } from 'react';
import { aiService } from '../../services/aiService';
import { SoAControl, SoAGuidanceResult } from '../../types/iso27001';
import { useI18n } from '../../context/I18nContext';
import { useTheme } from '../../context/ThemeContext';
import { Loader2, Sparkles, CheckCircle2, FileText, Shield, Users, ListChecks, X, Clock, AlertTriangle } from 'lucide-react';

interface SoAAdvisorCardProps {
  control: SoAControl;
  frameworkName?: string;
  frameworkContext?: {
    code?: string;
    type?: string;
    language?: string;
    operatorType?: string;
  };
  onApply: (justification: string, owner: string, steps: string[], requiredDocuments: string[], summary: string) => void;
  onClose: () => void;
}

export const SoAAdvisorCard: React.FC<SoAAdvisorCardProps> = ({ control, frameworkName, frameworkContext, onApply, onClose }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<SoAGuidanceResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGuidance = async () => {
      try {
        const guidance = await aiService.getImplementationGuidance(control, frameworkName, frameworkContext);
        setResult(guidance);
      } catch (err: any) {
        setError(err.message || t.aiAdvisor.error);
      } finally {
        setLoading(false);
      }
    };

    fetchGuidance();
  }, [control.id]);

  const handleApply = () => {
    if (result) {
      onApply(
        result.suggestedJustification, 
        result.suggestedOwnerRole, 
        result.implementationSteps,
        result.requiredDocuments,
        result.summary
      );
    }
  };

  if (loading) {
    return (
      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg flex items-center justify-center gap-2 text-purple-700 dark:text-purple-300">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm font-medium">{t.aiAdvisor.analyzing} {control.id}...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm">
        {t.aiAdvisor.error}: {error}
        <button onClick={onClose} className="ml-2 underline hover:no-underline">{t.aiAdvisor.close}</button>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div className="bg-purple-50 dark:bg-gray-800 border border-purple-200 dark:border-purple-700 rounded-lg p-4 mt-2">
      <div className="flex justify-between items-start mb-4">
        <h4 className="text-sm font-semibold text-purple-900 dark:text-purple-300 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          {t.aiAdvisor.title}
          {frameworkName && <span className="text-xs font-normal text-purple-600 dark:text-purple-400">({frameworkName})</span>}
        </h4>
        <button onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-4">
        {/* Summary */}
        <div className="bg-white dark:bg-gray-700 p-3 rounded border border-purple-100 dark:border-gray-600">
          <div className="text-sm text-gray-800 dark:text-gray-200">{result.summary}</div>
        </div>

        {/* Two Column Layout for Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white dark:bg-gray-700 p-3 rounded border border-purple-100 dark:border-gray-600">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold mb-2 flex items-center gap-1">
              <Shield className="w-3 h-3" /> {t.aiAdvisor.technicalControls}
            </div>
            <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
              {result.technicalControls.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="bg-white dark:bg-gray-700 p-3 rounded border border-purple-100 dark:border-gray-600">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold mb-2 flex items-center gap-1">
              <Users className="w-3 h-3" /> {t.aiAdvisor.organizationalControls}
            </div>
            <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
              {result.organizationalControls.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Implementation Steps */}
        <div className="bg-white dark:bg-gray-700 p-3 rounded border border-purple-100 dark:border-gray-600">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold mb-2 flex items-center gap-1">
            <ListChecks className="w-3 h-3" /> {t.aiAdvisor.implementationSteps}
          </div>
          <ol className="list-decimal list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
            {result.implementationSteps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>

        {/* Required Documents */}
        <div className="bg-white dark:bg-gray-700 p-3 rounded border border-purple-100 dark:border-gray-600">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold mb-2 flex items-center gap-1">
            <FileText className="w-3 h-3" /> {t.aiAdvisor.requiredDocumentation}
          </div>
          <div className="flex flex-wrap gap-2">
            {result.requiredDocuments.map((doc, i) => (
              <span key={i} className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-2 py-1 rounded border border-gray-200 dark:border-gray-500">
                {doc}
              </span>
            ))}
          </div>
        </div>

        {/* Suggestion Preview */}
        <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded border border-purple-200 dark:border-purple-700">
          <div className="text-xs text-purple-800 dark:text-purple-300 uppercase font-semibold mb-1">{t.aiAdvisor.suggestedJustification}</div>
          <div className="text-sm text-purple-900 dark:text-purple-200 italic mb-2">"{result.suggestedJustification}"</div>
          <div className="text-xs text-purple-800 dark:text-purple-300 uppercase font-semibold mb-1">{t.aiAdvisor.suggestedOwner}</div>
          <div className="text-sm text-purple-900 dark:text-purple-200 font-medium">{result.suggestedOwnerRole}</div>
        </div>

        {/* Compliance Timeline & Risk */}
        {(result.complianceDeadline || result.reviewFrequency || result.penaltyRisk) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {result.complianceDeadline && (
              <div className="bg-amber-50 dark:bg-amber-900/20 p-2.5 rounded border border-amber-200 dark:border-amber-800">
                <div className="text-[10px] text-amber-800 dark:text-amber-300 uppercase font-semibold mb-0.5 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Rok za usklađivanje
                </div>
                <div className="text-xs text-amber-900 dark:text-amber-200">{result.complianceDeadline}</div>
              </div>
            )}
            {result.reviewFrequency && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2.5 rounded border border-blue-200 dark:border-blue-800">
                <div className="text-[10px] text-blue-800 dark:text-blue-300 uppercase font-semibold mb-0.5 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Učestalost revizije
                </div>
                <div className="text-xs text-blue-900 dark:text-blue-200">{result.reviewFrequency}</div>
              </div>
            )}
            {result.penaltyRisk && (
              <div className="bg-red-50 dark:bg-red-900/20 p-2.5 rounded border border-red-200 dark:border-red-800">
                <div className="text-[10px] text-red-800 dark:text-red-300 uppercase font-semibold mb-0.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Rizik kazne
                </div>
                <div className="text-xs text-red-900 dark:text-red-200">{result.penaltyRisk}</div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-purple-200 dark:border-gray-600">
          <button 
            onClick={onClose}
            className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          >
            {t.aiAdvisor.dismiss}
          </button>
          <button 
            onClick={handleApply}
            className="text-xs bg-purple-600 dark:bg-purple-700 text-white px-3 py-1.5 rounded hover:bg-purple-700 dark:hover:bg-purple-600 flex items-center gap-1 shadow-sm transition-colors"
          >
            <CheckCircle2 className="w-3 h-3" /> {t.aiAdvisor.applySuggestions}
          </button>
        </div>
      </div>
    </div>
  );
};
